/* Seade's Print Shop — Gang Sheet Builder
 * Pure client-side canvas tool. No external dependencies.
 * Sizes/prices come from a connected Shopify product's variants
 * (data-product-json). The flattened sheet is uploaded as a cart
 * line-item file property; each source image name is listed too so
 * production staff have the originals referenced even before they
 * open the flattened file.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-gang-sheet-app]');
  if (!root) return;

  var PX_PER_IN = 18; // canvas render scale
  var WORKER_BASE_URL = 'https://seades-dtf-gang-sheet-builder.seadesprintshop.workers.dev';
  var lastUploadedKey = null;
  var variants = [];
  try { variants = JSON.parse(root.getAttribute('data-product-json') || '[]'); } catch (e) { variants = []; }

  var canvas = root.querySelector('[data-gs-canvas]');
  var ctx = canvas.getContext('2d');
  var sizeSelect = root.querySelector('[data-gs-size-select]');
  var fileInput = root.querySelector('#GsFileInput');
  var addImageBtn = root.querySelector('[data-gs-add-image]');
  var rotateBtn = root.querySelector('[data-gs-rotate]');
  var duplicateBtn = root.querySelector('[data-gs-duplicate]');
  var deleteBtn = root.querySelector('[data-gs-delete]');
  var sizeControlsEl = root.querySelector('[data-gs-size-controls]');
  var widthInInput = root.querySelector('[data-gs-width-in]');
  var heightInInput = root.querySelector('[data-gs-height-in]');
  var addToCartBtn = root.querySelector('[data-gs-add-to-cart]');
  var downloadBtn = root.querySelector('[data-gs-download]');
  var downloadStatusEl = root.querySelector('[data-gs-download-status]');
  var qtyInput = root.querySelector('[data-gs-quantity]');
  var statusEl = root.querySelector('[data-gs-status]');
  var warningEl = root.querySelector('[data-gs-warning]');
  var summarySize = root.querySelector('[data-gs-summary-size]');
  var summaryCount = root.querySelector('[data-gs-summary-count]');
  var summaryCoverage = root.querySelector('[data-gs-summary-coverage]');
  var summaryPrice = root.querySelector('[data-gs-summary-price]');

  var sheetWidthIn = parseFloat(root.getAttribute('data-sheet-width-in')) || 22;
  var sheetHeightIn = 24;
  var currentVariant = null;
  var images = []; // { img, name, x, y, w, h, rotationDeg }
  var selected = null;
  var drag = null; // { mode: 'move'|'resize', startX, startY, origX, origY, origW, origH }

  function parseHeightFromTitle(title) {
    // Expect variant titles like '24"' or '24in' — a single sheet length in
    // inches. Falls back to 24 if no number is found.
    var match = String(title).match(/(\d+(\.\d+)?)/);
    if (match) return parseFloat(match[1]);
    return 24;
  }

  function moneyFormat(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function populateSizes() {
    sizeSelect.innerHTML = '';
    variants.forEach(function (v, i) {
      var lengthIn = parseHeightFromTitle(v.public_title || v.title);
      var opt = document.createElement('option');
      opt.value = v.id;
      opt.textContent = sheetWidthIn + '" \u00d7 ' + lengthIn + '"';
      opt.dataset.height = lengthIn;
      opt.dataset.price = v.price;
      sizeSelect.appendChild(opt);
      if (i === 0) currentVariant = v;
    });
    if (variants.length) {
      sheetHeightIn = parseHeightFromTitle(variants[0].public_title || variants[0].title);
    }
  }

  function resizeCanvas() {
    canvas.width = sheetWidthIn * PX_PER_IN;
    canvas.height = sheetHeightIn * PX_PER_IN;
    draw();
  }

  /* ---------- Precise inch-based sizing ---------- */
  // Width and Height inputs are the precise sizing mechanism, mapped to the
  // real sheet dimensions (sheetWidthIn / sheetHeightIn) — not arbitrary
  // canvas pixels. The existing drag/resize-handle interaction still works
  // and stays in sync with these fields in both directions.
  function syncSizeInputs() {
    if (widthInInput) widthInInput.max = sheetWidthIn;
    if (heightInInput) heightInInput.max = sheetHeightIn;
    if (!sizeControlsEl) return;
    if (!selected) {
      sizeControlsEl.hidden = true;
      return;
    }
    sizeControlsEl.hidden = false;
    widthInInput.value = (selected.w / PX_PER_IN).toFixed(2);
    heightInInput.value = (selected.h / PX_PER_IN).toFixed(2);
  }

  function applySizeFromInches(source) {
    if (!selected) return;
    var ratio = selected.h / selected.w; // current aspect ratio, preserved by default
    var wPx, hPx;
    if (source === 'width') {
      var wIn = parseFloat(widthInInput.value);
      if (!wIn || wIn <= 0) return;
      wPx = wIn * PX_PER_IN;
      hPx = wPx * ratio;
    } else {
      var hIn = parseFloat(heightInInput.value);
      if (!hIn || hIn <= 0) return;
      hPx = hIn * PX_PER_IN;
      wPx = hPx / ratio;
    }

    // Enforce boundaries: artwork cannot exceed the sheet, mapped to the
    // real sheet dimensions and the image's current position on it.
    var maxW = Math.min(canvas.width - selected.x, sheetWidthIn * PX_PER_IN);
    var maxH = Math.min(canvas.height - selected.y, sheetHeightIn * PX_PER_IN);
    var scale = Math.min(1, maxW / wPx, maxH / hPx);
    if (scale > 0 && scale < 1) {
      wPx *= scale;
      hPx *= scale;
    }

    selected.w = Math.max(12, wPx);
    selected.h = Math.max(12, hPx);
    draw();
    updateSummary();
    syncSizeInputs();
  }

  if (widthInInput) {
    widthInInput.addEventListener('input', function () { applySizeFromInches('width'); });
  }
  if (heightInInput) {
    heightInInput.addEventListener('input', function () { applySizeFromInches('height'); });
  }

  sizeSelect.addEventListener('change', function () {
    var opt = sizeSelect.options[sizeSelect.selectedIndex];
    currentVariant = variants.filter(function (v) { return String(v.id) === opt.value; })[0];
    sheetHeightIn = parseFloat(opt.dataset.height) || 24;
    resizeCanvas();
    updateSummary();
    syncSizeInputs();
  });

  /* ---------- Image loading ---------- */
  addImageBtn.addEventListener('click', function () { fileInput.click(); });

  fileInput.addEventListener('change', function () {
    Array.prototype.forEach.call(fileInput.files, function (file) {
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          var maxW = 4 * PX_PER_IN; // default placement width, 4in
          var scale = Math.min(1, maxW / img.width);
          var w = img.width * scale;
          var h = img.height * scale;
          var entry = {
            img: img,
            name: file.name,
            x: (canvas.width - w) / 2 + images.length * 10,
            y: (canvas.height - h) / 2 + images.length * 10,
            w: w, h: h,
            rotationDeg: 0
          };
          images.push(entry);
          selected = entry;
          draw();
          updateSummary();
          syncSizeInputs();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
    fileInput.value = '';
  });

  /* ---------- Drawing ---------- */
  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    images.forEach(function (item) {
      ctx.save();
      ctx.translate(item.x + item.w / 2, item.y + item.h / 2);
      ctx.rotate((item.rotationDeg * Math.PI) / 180);
      ctx.drawImage(item.img, -item.w / 2, -item.h / 2, item.w, item.h);
      ctx.restore();

      if (item === selected) {
        ctx.save();
        ctx.strokeStyle = '#D63C28';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(item.x, item.y, item.w, item.h);
        ctx.restore();
        // resize handle
        ctx.fillStyle = '#D63C28';
        ctx.fillRect(item.x + item.w - 8, item.y + item.h - 8, 12, 12);
      }
    });
  }

  /* ---------- Pointer interaction ---------- */
  function getPos(evt) {
    var rect = canvas.getBoundingClientRect();
    var scaleX = canvas.width / rect.width;
    var scaleY = canvas.height / rect.height;
    var point = evt.touches ? evt.touches[0] : evt;
    return { x: (point.clientX - rect.left) * scaleX, y: (point.clientY - rect.top) * scaleY };
  }

  function hitTest(pos) {
    for (var i = images.length - 1; i >= 0; i--) {
      var item = images[i];
      if (pos.x >= item.x && pos.x <= item.x + item.w && pos.y >= item.y && pos.y <= item.y + item.h) {
        return item;
      }
    }
    return null;
  }

  function isOnHandle(pos, item) {
    return item && pos.x >= item.x + item.w - 12 && pos.x <= item.x + item.w + 4 &&
           pos.y >= item.y + item.h - 12 && pos.y <= item.y + item.h + 4;
  }

  function startInteraction(evt) {
    evt.preventDefault();
    var pos = getPos(evt);
    if (selected && isOnHandle(pos, selected)) {
      drag = { mode: 'resize', startX: pos.x, startY: pos.y, origW: selected.w, origH: selected.h };
      return;
    }
    var hit = hitTest(pos);
    selected = hit;
    if (hit) {
      drag = { mode: 'move', startX: pos.x, startY: pos.y, origX: hit.x, origY: hit.y };
    }
    draw();
    updateSummary();
    syncSizeInputs();
  }

  function moveInteraction(evt) {
    if (!drag || !selected) return;
    evt.preventDefault();
    var pos = getPos(evt);
    if (drag.mode === 'move') {
      selected.x = drag.origX + (pos.x - drag.startX);
      selected.y = drag.origY + (pos.y - drag.startY);
    } else if (drag.mode === 'resize') {
      var deltaW = pos.x - drag.startX;
      var ratio = drag.origH / drag.origW;
      selected.w = Math.max(12, drag.origW + deltaW);
      selected.h = selected.w * ratio;
    }
    draw();
    updateSummary();
    if (drag.mode === 'resize') syncSizeInputs();
  }

  function endInteraction() { drag = null; }

  canvas.addEventListener('mousedown', startInteraction);
  canvas.addEventListener('touchstart', startInteraction, { passive: false });
  window.addEventListener('mousemove', moveInteraction);
  window.addEventListener('touchmove', moveInteraction, { passive: false });
  window.addEventListener('mouseup', endInteraction);
  window.addEventListener('touchend', endInteraction);

  rotateBtn.addEventListener('click', function () {
    if (!selected) return;
    selected.rotationDeg = (selected.rotationDeg + 90) % 360;
    draw();
  });

  duplicateBtn.addEventListener('click', function () {
    if (!selected) return;
    var clone = Object.assign({}, selected, { x: selected.x + 14, y: selected.y + 14 });
    images.push(clone);
    selected = clone;
    draw();
    updateSummary();
    syncSizeInputs();
  });

  deleteBtn.addEventListener('click', function () {
    if (!selected) return;
    images = images.filter(function (i) { return i !== selected; });
    selected = null;
    draw();
    updateSummary();
    syncSizeInputs();
  });

  /* ---------- Summary / pricing ---------- */
  function updateSummary() {
    var opt = sizeSelect.options[sizeSelect.selectedIndex];
    summarySize.textContent = opt ? opt.textContent : '—';
    summaryCount.textContent = images.length;

    var sheetArea = canvas.width * canvas.height;
    var usedArea = images.reduce(function (sum, i) { return sum + i.w * i.h; }, 0);
    var coveragePct = sheetArea ? Math.min(100, Math.round((usedArea / sheetArea) * 100)) : 0;
    summaryCoverage.textContent = coveragePct + '%';

    var price = currentVariant ? parseInt(currentVariant.price, 10) : 0;
    summaryPrice.textContent = currentVariant ? moneyFormat(price) : '—';

    var outOfBounds = images.some(function (i) {
      return i.x < 0 || i.y < 0 || i.x + i.w > canvas.width || i.y + i.h > canvas.height;
    });
    if (outOfBounds) {
      warningEl.hidden = false;
      warningEl.textContent = 'One or more images extend past the sheet edge — drag them fully inside before ordering.';
    } else {
      warningEl.hidden = true;
    }
  }

  /* ---------- Wasabi upload (via Cloudflare Worker) ---------- */
  function uploadFlattenedSheetToWasabi(blob) {
    var key = 'gang-sheets/gang-sheet-' + Date.now() + '.png';
    return fetch(WORKER_BASE_URL + '/upload-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: key, contentType: 'image/png' })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('upload-url-request-failed');
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.uploadUrl) throw new Error('upload-url-response-missing-uploadUrl');
        return fetch(data.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/png' },
          body: blob
        }).then(function (putRes) {
          if (!putRes.ok) throw new Error('wasabi-put-failed:' + putRes.status);
          return data.key || key;
        });
      });
  }

  function setDownloadStatus(msg, state) {
    if (!downloadStatusEl) return;
    downloadStatusEl.textContent = msg;
    if (state) downloadStatusEl.setAttribute('data-state', state);
    else downloadStatusEl.removeAttribute('data-state');
  }

  if (downloadBtn) {
    downloadBtn.addEventListener('click', function () {
      if (!lastUploadedKey) return;
      downloadBtn.disabled = true;
      setDownloadStatus('Preparing download…', '');
      fetch(WORKER_BASE_URL + '/download-url?key=' + encodeURIComponent(lastUploadedKey))
        .then(function (res) {
          if (!res.ok) throw new Error('download-url-request-failed');
          return res.json();
        })
        .then(function (data) {
          if (!data || !data.success || !data.downloadUrl) throw new Error('download-url-response-invalid');
          window.open(data.downloadUrl, '_blank');
          setDownloadStatus('', '');
          downloadBtn.disabled = false;
        })
        .catch(function () {
          setDownloadStatus('Could not generate a download link. Please try again.', 'error');
          downloadBtn.disabled = false;
        });
    });
  }

  /* ---------- PNG pHYs metadata (300 DPI) ---------- */
  // canvas.toBlob() cannot write physical-dimension metadata, so the exported
  // PNG carried no pHYs chunk and most software then assumed 72 DPI - a
  // 22in x 12in sheet opened as 91.7in x 50in. This inserts a standards
  // compliant pHYs chunk immediately after IHDR declaring the sheet's ACTUAL
  // export resolution on both axes. That value is now dynamic: since the sheet
  // may be rasterised below 300 DPI when the artwork does not carry that much
  // detail, a hard-coded 300 DPI tag would make every such sheet print at the
  // wrong physical size. It is a pure metadata insertion performed on the
  // already-encoded PNG bytes: pixel data, colour type, alpha, compression,
  // canvas dimensions, placement, scaling and sheet size are all untouched,
  // and the file grows by exactly 21 bytes. If anything at all goes wrong the
  // original blob is passed through unchanged, so metadata can never block an
  // export.
  // Pixels per metre for a given DPI. 300 -> 11811, 200 -> 7874, 150 -> 5906,
  // 100 -> 3937. This is now derived from the DPI the sheet was actually
  // rasterised at, never hard-coded, so the declared physical size always
  // matches the real raster.
  function pngPixelsPerMetre(dpi) {
    return Math.round(dpi * 39.37007874);
  }

  var PNG_CRC_TABLE = (function () {
    var table = new Int32Array(256);
    for (var n = 0; n < 256; n++) {
      var c = n;
      for (var k = 0; k < 8; k++) {
        c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
      }
      table[n] = c;
    }
    return table;
  })();

  function pngCrc32(bytes) {
    var c = -1;
    for (var i = 0; i < bytes.length; i++) {
      c = PNG_CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    }
    return (c ^ -1) >>> 0;
  }

  function addPngPhysChunk(blob, dpi, callback) {
    var reader = new FileReader();
    reader.onerror = function () { callback(blob); };
    reader.onload = function () {
      try {
        var bytes = new Uint8Array(reader.result);
        var signature = [137, 80, 78, 71, 13, 10, 26, 10];
        for (var s = 0; s < 8; s++) {
          if (bytes[s] !== signature[s]) { callback(blob); return; }
        }
        // IHDR is always the first chunk: 4 length + 4 type + data + 4 CRC.
        var ihdrLength = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(8);
        var insertAt = 8 + 12 + ihdrLength;
        if (insertAt + 8 > bytes.length) { callback(blob); return; }
        // Never write a second pHYs chunk.
        var nextType = String.fromCharCode(bytes[insertAt + 4], bytes[insertAt + 5], bytes[insertAt + 6], bytes[insertAt + 7]);
        if (nextType === 'pHYs') { callback(blob); return; }

        var ppm = pngPixelsPerMetre(dpi);
        var chunk = new Uint8Array(21); // 4 length + 4 type + 9 data + 4 CRC
        var view = new DataView(chunk.buffer);
        view.setUint32(0, 9);
        chunk[4] = 0x70; chunk[5] = 0x48; chunk[6] = 0x59; chunk[7] = 0x73; // 'pHYs'
        view.setUint32(8, ppm);   // pixels per unit, X axis
        view.setUint32(12, ppm);  // pixels per unit, Y axis
        chunk[16] = 1;            // unit specifier: metre
        view.setUint32(17, pngCrc32(chunk.subarray(4, 17)));

        var out = new Uint8Array(bytes.length + chunk.length);
        out.set(bytes.subarray(0, insertAt), 0);
        out.set(chunk, insertAt);
        out.set(bytes.subarray(insertAt), insertAt + chunk.length);
        callback(new Blob([out], { type: 'image/png' }));
      } catch (e) {
        callback(blob);
      }
    };
    reader.readAsArrayBuffer(blob);
  }

  /* ---------- High-resolution transparent export ---------- */
  // Produces a separate, ephemeral canvas at 300 DPI (300px/in), entirely
  // apart from the on-screen preview canvas — which stays at PX_PER_IN for
  // performance and is never resized. The preview's white background fill
  // in draw() is never applied here: this canvas starts fully transparent
  // (its default state) and only the artwork itself is drawn onto it, so
  // the exported PNG's alpha channel is preserved.
  var EXPORT_DPI = 300;     // ceiling - never exceeded
  var MIN_EXPORT_DPI = 72;  // floor - guards against a pathological upload

  // Source-aware sheet resolution.
  //
  // The sheet raster used to be a flat 300 DPI, so artwork placed larger than
  // its own native pixels supported was stretched onto invented pixels. That
  // interpolation was measured to be bit-for-bit reversible - it added no
  // source information whatsoever, only file size.
  //
  // Instead the sheet is rasterised at the highest resolution the artwork
  // genuinely carries, capped at 300 DPI. Both axes are checked and the larger
  // requirement wins, so a rotated or non-square placement is never treated as
  // having more detail than it has. The maximum across all placed images is
  // used, so one high-resolution image always keeps the whole sheet at its
  // resolution and no image is ever rasterised below its native pixels.
  //
  // Physical geometry is completely untouched: position, size, rotation and
  // the sheet's real dimensions in inches are all unchanged, and the pHYs
  // chunk declares the matching DPI so the file still prints at 22in x Nin.
  function computeSheetDpi() {
    var required = 0;
    images.forEach(function (item) {
      var widthIn = item.w / PX_PER_IN;
      var heightIn = item.h / PX_PER_IN;
      var natW = item.img.naturalWidth || item.img.width;
      var natH = item.img.naturalHeight || item.img.height;
      if (widthIn > 0 && natW) required = Math.max(required, natW / widthIn);
      if (heightIn > 0 && natH) required = Math.max(required, natH / heightIn);
    });
    if (!required) return EXPORT_DPI;
    return Math.max(MIN_EXPORT_DPI, Math.min(EXPORT_DPI, Math.ceil(required)));
  }

  function buildExportBlob(callback) {
    var sheetDpi = computeSheetDpi();
    var exportWidth = Math.round(sheetWidthIn * sheetDpi);
    var exportHeight = Math.round(sheetHeightIn * sheetDpi);
    var exportScale = sheetDpi / PX_PER_IN;

    var exportCanvas = document.createElement('canvas');
    exportCanvas.width = exportWidth;
    exportCanvas.height = exportHeight;
    var exportCtx = exportCanvas.getContext('2d');
    // Deliberately no fillRect: the canvas is transparent by default, and
    // must stay that way.

    images.forEach(function (item) {
      var w = item.w * exportScale;
      var h = item.h * exportScale;
      var x = item.x * exportScale;
      var y = item.y * exportScale;
      exportCtx.save();
      exportCtx.translate(x + w / 2, y + h / 2);
      exportCtx.rotate((item.rotationDeg * Math.PI) / 180);
      exportCtx.drawImage(item.img, -w / 2, -h / 2, w, h);
      exportCtx.restore();
    });

    exportCanvas.toBlob(function (blob) {
      // A null blob means the browser refused the canvas (see the guard in the
      // add-to-cart handler). Pass it straight through so the caller can stop.
      if (!blob) { callback(null); return; }
      addPngPhysChunk(blob, sheetDpi, callback);
    }, 'image/png');
  }

  /* ---------- Add to cart ---------- */
  addToCartBtn.addEventListener('click', function () {
    if (!currentVariant) {
      setStatus('Select a sheet size first.', 'error');
      return;
    }
    if (images.length === 0) {
      setStatus('Add at least one image before ordering.', 'error');
      return;
    }
    var outOfBounds = images.some(function (i) {
      return i.x < 0 || i.y < 0 || i.x + i.w > canvas.width || i.y + i.h > canvas.height;
    });
    if (outOfBounds) {
      setStatus('Fix images that extend past the sheet edge first.', 'error');
      return;
    }

    setStatus('Preparing your sheet…', '');
    addToCartBtn.disabled = true;

    // Redraw without the selection outline for a clean export.
    var prevSelected = selected;
    selected = null;
    draw();

    buildExportBlob(function (blob) {
      selected = prevSelected;
      draw();

      // canvas.toBlob() returns null when the export canvas exceeds the
      // maximum canvas size the browser allows - measured on this store at
      // sheet lengths of 144in and above (22in x 144in at 300 DPI is
      // 6600 x 43200 px). Without this guard the null was handed to the
      // uploader, which PUT an empty body to Wasabi, created a 0-byte object,
      // and still added the line to the cart, so the customer paid and
      // production received an empty file with no error anywhere. Stop here
      // instead: nothing is uploaded, nothing is added to the cart, the
      // existing status area explains why, and the button is re-enabled so the
      // customer can choose a smaller size and retry.
      if (!blob) {
        console.error(
          '[Gang Sheet Builder] canvas.toBlob() returned null for a ' +
          Math.round(sheetWidthIn * computeSheetDpi()) + ' x ' + Math.round(sheetHeightIn * computeSheetDpi()) +
          ' px export canvas (' + sheetWidthIn + 'in x ' + sheetHeightIn + 'in at ' + computeSheetDpi() +
          ' DPI), which exceeds the maximum canvas size this browser allows. ' +
          'Nothing was uploaded and nothing was added to the cart.'
        );
        setStatus('Your gang sheet is too large for this browser to export. Please try a smaller sheet size or use a desktop browser.', 'error');
        setDownloadStatus('', '');
        addToCartBtn.disabled = false;
        return;
      }

      // The cart operation now waits for this upload to resolve before
      // building FormData, so properties[Wasabi Key] is always the CURRENT
      // sheet's key — never a stale value from a previous upload. If this
      // fails, the cart-add is stopped entirely: no item is added with a
      // blank or stale key.
      if (downloadBtn) downloadBtn.hidden = true;
      setDownloadStatus('Uploading to storage…', '');

      uploadFlattenedSheetToWasabi(blob)
        .then(function (currentKey) {
          lastUploadedKey = currentKey; // still tracked for the Download button
          if (downloadBtn) downloadBtn.hidden = false;
          setDownloadStatus('', '');

          // Resolve the actual usable download URL for this key, using the
          // exact same mechanism the Download button already relies on —
          // currentKey alone is just a storage path, not a usable URL.
          return fetch(WORKER_BASE_URL + '/download-url?key=' + encodeURIComponent(currentKey))
            .then(function (res) {
              if (!res.ok) throw new Error('download-url-request-failed');
              return res.json();
            })
            .then(function (data) {
              if (!data || !data.success || !data.downloadUrl) throw new Error('download-url-response-invalid');

              var formData = new FormData();
              formData.append('id', currentVariant.id);
              formData.append('quantity', qtyInput.value || 1);
              formData.append('properties[Gang Sheet Layout]', data.downloadUrl);
              formData.append('properties[Source Files]', images.map(function (i) { return i.name; }).join(', '));
              formData.append('properties[Images Placed]', String(images.length));
              formData.append('properties[Wasabi Key]', currentKey);

              return fetch('/cart/add.js', { method: 'POST', body: formData })
                .then(function (res) {
                  if (!res.ok) throw new Error('add-failed');
                  return res.json();
                })
                .then(function () {
                  setStatus('Added to cart!', 'success');
                  return fetch('/cart.js');
                })
                .then(function (res) { return res.json(); })
                .then(function (cart) {
                  document.querySelectorAll('[data-cart-count]').forEach(function (el) {
                    el.textContent = cart.item_count;
                  });
                  if (window.theme && window.theme.miniCart) {
                    window.theme.miniCart.updateElements();
                    window.theme.miniCart.generateCart();
                  }
                  addToCartBtn.disabled = false;
                })
                .catch(function () {
                  setStatus('Something went wrong adding this to your cart. Please try again.', 'error');
                  addToCartBtn.disabled = false;
                });
            });
        })
        .catch(function () {
          setStatus('Could not upload your gang sheet. Please try again.', 'error');
          setDownloadStatus('', '');
          addToCartBtn.disabled = false;
        });
    });
  });

  function setStatus(msg, state) {
    statusEl.textContent = msg;
    if (state) statusEl.setAttribute('data-state', state);
    else statusEl.removeAttribute('data-state');
  }

  /* ---------- Init ---------- */
  populateSizes();
  resizeCanvas();
  updateSummary();
  syncSizeInputs();
})();
